import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, AuthSession } from '../../types/UserInterface';
import { loginThunk, logoutThunk, refreshSessionThunk } from './authThunks';

interface AuthState {
  user:             User | null;
  loginToken:       string | null;
  tenantToken:      string | null;
  apiKey:           string | null;
  isAuthenticated:  boolean;
  loading:          boolean;
  error:            string | null;
}

function getInitialState(): AuthState {
  const loginToken  = localStorage.getItem('cilpron_token');
  const tenantToken = localStorage.getItem('cilpron_tenant_token');
  const apiKey      = localStorage.getItem('cilpron_api_key');

  if (!loginToken || !tenantToken) {
    return {
      user: null, loginToken: null, tenantToken: null,
      apiKey: null, isAuthenticated: false, loading: false, error: null,
    };
  }

  try {
    const { isTokenExpired, extractUserFromToken } = require('../../utils/jwt');
    if (isTokenExpired(loginToken)) {
      localStorage.removeItem('cilpron_token');
      localStorage.removeItem('cilpron_tenant_token');
      localStorage.removeItem('cilpron_api_key');
      return {
        user: null, loginToken: null, tenantToken: null,
        apiKey: null, isAuthenticated: false, loading: false, error: null,
      };
    }
    const user = extractUserFromToken(loginToken);
    return {
      user, loginToken, tenantToken,
      apiKey: apiKey ?? null, isAuthenticated: !!user, loading: false, error: null,
    };
  } catch {
    return {
      user: null, loginToken: null, tenantToken: null,
      apiKey: null, isAuthenticated: false, loading: false, error: null,
    };
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState() as AuthState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setCredentials(state, action: PayloadAction<AuthSession>) {
      state.user          = action.payload.user;
      state.loginToken    = action.payload.loginToken;
      state.tenantToken   = action.payload.tenantToken;
      state.apiKey        = action.payload.apiKey;
      state.isAuthenticated = true;
      state.error         = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(loginThunk.fulfilled, (state, action: PayloadAction<AuthSession>) => {
        state.loading         = false;
        state.isAuthenticated = true;
        state.user            = action.payload.user;
        state.loginToken      = action.payload.loginToken;
        state.tenantToken     = action.payload.tenantToken;
        state.apiKey          = action.payload.apiKey;
        state.error           = null;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading         = false;
        state.isAuthenticated = false;
        state.user            = null;
        state.loginToken      = null;
        state.tenantToken     = null;
        state.apiKey          = null;
        state.error           = action.payload as string;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user            = null;
        state.loginToken      = null;
        state.tenantToken     = null;
        state.apiKey          = null;
        state.error           = null;
        state.loading         = false;
      })
      .addCase(refreshSessionThunk.fulfilled, (state, action: PayloadAction<AuthSession>) => {
        state.user            = action.payload.user;
        state.loginToken      = action.payload.loginToken;
        state.tenantToken     = action.payload.tenantToken;
        state.apiKey          = action.payload.apiKey;
        state.isAuthenticated = true;
      })
      .addCase(refreshSessionThunk.rejected, (state) => {
        state.isAuthenticated = false;
        state.user            = null;
        state.loginToken      = null;
        state.tenantToken     = null;
        state.apiKey          = null;
      });
  },
});

export const { clearError, setCredentials } = authSlice.actions;
export default authSlice.reducer;